"use client";

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { Button } from 'ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from 'ui/dialog';
import { Textarea } from 'ui/textarea';
import { Alert, AlertDescription } from 'ui/alert';
import { Badge } from 'ui/badge';
import {
    Bug,
    Camera,
    Send,
    Loader,
    CheckCircle,
    AlertTriangle,
    X,
    Info
} from 'lucide-react';
import { BugReportData, BugReportService, BugReportContext } from '@/lib/services/bug-report-service';
import { ScreenshotCapture } from '@/lib/utils/screenshot-capture';
import { toast } from 'sonner';

interface BugReportModalProps {
    isOpen: boolean;
    onClose: () => void;
    errorContext: BugReportContext;
}

interface SubmissionState {
    status: 'idle' | 'capturing' | 'submitting' | 'success' | 'error';
    message?: string;
}

export function BugReportModal({ isOpen, onClose, errorContext }: BugReportModalProps) {
    const t = useTranslations('BugReport');
    const [userInput, setUserInput] = useState('');
    const [screenshot, setScreenshot] = useState<string | null>(null);
    const [submissionState, setSubmissionState] = useState<SubmissionState>({ status: 'idle' });
    const [errorData, setErrorData] = useState<Omit<BugReportData, 'userInput' | 'screenshot'> | null>(null);
    // Track a capture token so we can ignore late results
    const [captureToken, setCaptureToken] = useState(0);

    // Collect error data when modal opens
    useEffect(() => {
        if (isOpen && !errorData) {
            const data = BugReportService.collectErrorData(errorContext);
            setErrorData(data);
        }
    }, [isOpen, errorContext, errorData]);

    // Auto-capture screenshot when modal opens
    useEffect(() => {
        if (isOpen && !screenshot && submissionState.status === 'idle') {
            captureScreenshot();
        }
    }, [isOpen, screenshot, submissionState.status]);

    const captureScreenshot = async () => {
        // Increment token to invalidate any in-flight previous capture
        const token = captureToken + 1;
        setCaptureToken(token);
        setSubmissionState({ status: 'capturing', message: t('capturingScreenshot') });

        // Timeout fallback (5s)
        const timeoutMs = 5000;
        let timedOut = false;
        const timer = setTimeout(() => {
            timedOut = true;
            // Leave screenshot as null and allow user to submit
            setSubmissionState({ status: 'idle', message: t('screenshotCaptureFailed') });
        }, timeoutMs);

        try {
            const screenshotData = await ScreenshotCapture.captureScreenshot({
                quality: 0.8,
                format: 'jpeg',
                maxWidth: 1920,
                maxHeight: 1080,
                excludeElements: ['.bug-report-modal', '[data-bug-report-exclude]']
            });
            if (timedOut) return; // Ignore late result if we already timed out
            // Ignore if a newer capture started
            if (token !== captureToken + 0) {
                return;
            }
            setScreenshot(screenshotData);
            setSubmissionState({ status: 'idle' });
        } catch (error) {
            if (!timedOut) {
                console.error('Screenshot capture failed:', error);
                setSubmissionState({ status: 'idle', message: t('screenshotCaptureFailed') });
            }
        } finally {
            clearTimeout(timer);
        }
    };

    const handleSubmit = async () => {
        if (!errorData) {
            toast.error(t('errorDataMissing'));
            return;
        }

        if (!userInput.trim()) {
            toast.error(t('pleaseProvideDescription'));
            return;
        }

        setSubmissionState({ status: 'submitting', message: t('submittingReport') });

        try {
            // Prepare data and ensure screenshot is reasonably small
            let screenshotToSend = screenshot || undefined;
            if (screenshotToSend) {
                const size = ScreenshotCapture.getDataUrlSize(screenshotToSend);
                // Compress over 2MB
                if (size > 2 * 1024 * 1024) {
                    try {
                        screenshotToSend = await ScreenshotCapture.compressScreenshot(screenshotToSend, 2 * 1024 * 1024);
                    } catch (_e) {
                        console.warn('Screenshot compression failed, will send without screenshot');
                        screenshotToSend = undefined;
                    }
                }
                // If still too big (safety), drop it
                if (screenshotToSend && ScreenshotCapture.getDataUrlSize(screenshotToSend) > 2 * 1024 * 1024) {
                    console.warn('Screenshot still too large after compression, dropping it');
                    screenshotToSend = undefined;
                }
            }

            const bugReportData: BugReportData = {
                ...errorData,
                userInput: userInput.trim(),
                screenshot: screenshotToSend
            };

            // Validate data
            const validation = BugReportService.validateBugReportData(bugReportData);
            if (!validation.valid) {
                throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
            }

            // Submit to API with timeout (15s) to avoid hanging on slow email send
            const controller = new AbortController();
            const timeout = setTimeout(() => controller.abort(), 45000);
            let response: Response;
            try {
                response = await fetch('/api/bug-report', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(bugReportData),
                    signal: controller.signal,
                });
            } finally {
                clearTimeout(timeout);
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
                throw new Error(errorData.message || `HTTP ${response.status}`);
            }

            const result = await response.json();

            setSubmissionState({
                status: 'success',
                message: t('reportSubmittedSuccessfully', { reportId: result.reportId })
            });

            toast.success(t('bugReportSent'));

            // Auto-close after success
            setTimeout(() => {
                onClose();
            }, 3000);

        } catch (error) {
            console.error('Bug report submission failed:', error);
            const isAbort = error instanceof Error && error.name === 'AbortError';
            setSubmissionState({
                status: 'error',
                message: isAbort ? 'Timed out waiting for email send. Please try again; if the issue persists, submit without a screenshot.' : t('submissionFailed', {
                    error: error instanceof Error ? error.message : 'Unknown error'
                })
            });
            toast.error(isAbort ? 'Bug report request timed out.' : t('failedToSendBugReport'));
        }
    };

    const handleClose = () => {
        if (submissionState.status === 'submitting') {
            return; // Prevent closing during submission
        }

        setUserInput('');
        setScreenshot(null);
        setSubmissionState({ status: 'idle' });
        setErrorData(null);
        onClose();
    };

    // Allow submission unless we're actively submitting to the server
    const canSubmit = submissionState.status !== 'submitting' && userInput.trim().length > 0;

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="bug-report-modal sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Bug className="h-5 w-5 text-red-600" />
                        {t('reportBug')}
                    </DialogTitle>
                    <DialogDescription>
                        {t('reportBugDescription')}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    {/* Error Summary */}
                    {errorData && (
                        <Alert>
                            <AlertTriangle className="h-4 w-4" />
                            <AlertDescription>
                                <div className="space-y-2">
                                    <p className="font-medium">{t('errorSummary')}:</p>
                                    <p className="text-sm bg-muted p-2 rounded font-mono">
                                        {errorData.error.name}: {errorData.error.message}
                                    </p>
                                    <div className="flex flex-wrap gap-1 mt-2">
                                        <Badge variant="outline" className="text-xs">
                                            ID: {errorData.reportId.slice(-8)}
                                        </Badge>
                                        {errorData.chatContext.threadId && (
                                            <Badge variant="outline" className="text-xs">
                                                Thread: {errorData.chatContext.threadId.slice(-8)}
                                            </Badge>
                                        )}
                                        {errorData.chatContext.agentId && (
                                            <Badge variant="outline" className="text-xs">
                                                Agent: {errorData.chatContext.agentId.slice(-8)}
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                            </AlertDescription>
                        </Alert>
                    )}

                    {/* Screenshot Status */}
                    <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                        <Camera className="h-4 w-4" />
                        <span className="text-sm flex-1">
                            {submissionState.status === 'capturing' ? (
                                <span className="flex items-center gap-2">
                                    <Loader className="h-3 w-3 animate-spin" />
                                    {t('capturingScreenshot')}
                                </span>
                            ) : screenshot ? (
                                <span className="flex items-center gap-2">
                                    <CheckCircle className="h-3 w-3 text-green-600" />
                                    {t('screenshotCaptured')}
                                </span>
                            ) : (
                                <span className="flex items-center gap-2">
                                    <X className="h-3 w-3 text-red-600" />
                                    {t('screenshotFailed')}
                                </span>
                            )}
                        </span>
                        {!screenshot && submissionState.status !== 'capturing' && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={captureScreenshot}
                            >
                                {t('retryScreenshot')}
                            </Button>
                        )}
                        {submissionState.status === 'capturing' && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setSubmissionState({ status: 'idle', message: t('screenshotCaptureFailed') })}
                            >
                                {t('retryScreenshot')}/Skip
                            </Button>
                        )}
                    </div>

                    {/* User Input */}
                    <div className="space-y-2">
                        <label htmlFor="bug-description" className="text-sm font-medium">
                            {t('additionalInformation')} *
                        </label>
                        <Textarea
                            id="bug-description"
                            placeholder={t('describeWhatHappened')}
                            value={userInput}
                            onChange={(e) => setUserInput(e.target.value)}
                            className="min-h-[100px]"
                            disabled={submissionState.status === 'submitting'}
                        />
                        <p className="text-xs text-muted-foreground">
                            {t('descriptionHelp')}
                        </p>
                    </div>

                    {/* Submission Status */}
                    {submissionState.message && (
                        <Alert variant={submissionState.status === 'error' ? 'destructive' : 'default'}>
                            {submissionState.status === 'success' ? (
                                <CheckCircle className="h-4 w-4" />
                            ) : submissionState.status === 'error' ? (
                                <AlertTriangle className="h-4 w-4" />
                            ) : (
                                <Info className="h-4 w-4" />
                            )}
                            <AlertDescription>
                                {submissionState.message}
                            </AlertDescription>
                        </Alert>
                    )}
                </div>

                <DialogFooter>
                    <Button
                        variant="ghost"
                        onClick={handleClose}
                        disabled={submissionState.status === 'submitting'}
                    >
                        {submissionState.status === 'success' ? t('close') : t('cancel')}
                    </Button>
                    {submissionState.status !== 'success' && (
                        <Button
                            onClick={handleSubmit}
                            disabled={!canSubmit || submissionState.status === 'submitting'}
                            className="gap-2"
                        >
                            {submissionState.status === 'submitting' ? (
                                <Loader className="h-4 w-4 animate-spin" />
                            ) : (
                                <Send className="h-4 w-4" />
                            )}
                            {submissionState.status === 'submitting' ? t('sending') : t('sendReport')}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}